import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProdStatusBadge } from "./Production";

export default function ProductionReports() {
  const [tab, setTab] = useState("production");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [prod, setProd] = useState(null);
  const [wages, setWages] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [waste, setWaste] = useState(null);

  const load = useCallback(async () => {
    const q = `${from ? `date_from=${from}` : ""}${from && to ? "&" : ""}${to ? `date_to=${to}` : ""}`;
    const [a, b, c, d] = await Promise.all([
      api.get(`/reports/production?${q}`), api.get(`/reports/production-wages?${q}`),
      api.get(`/reports/production-materials?${q}`), api.get(`/reports/production-waste?${q}`),
    ]);
    setProd(a.data); setWages(b.data); setMaterials(c.data); setWaste(d.data);
  }, [from, to]);
  useEffect(() => { load().catch((e) => console.error(e)); }, [load]);

  return (
    <div data-testid="page-production-reports" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Produksi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Laporan & Rekap Upah Produksi</h1>
      </div>

      <Card>
        <div className="p-4 flex gap-3 items-end flex-wrap">
          <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="filter-date-from" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="filter-date-to" /></div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="production" data-testid="tab-rep-production">Produksi</TabsTrigger>
          <TabsTrigger value="wages" data-testid="tab-rep-wages">Upah Operator</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-rep-materials">Bahan</TabsTrigger>
          <TabsTrigger value="waste" data-testid="tab-rep-waste">Waste</TabsTrigger>
        </TabsList>

        <TabsContent value="production" className="mt-4">
          <Card>
            <table className="w-full text-sm" data-testid="rep-production">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Tanggal</th><th>No. Produksi</th><th>Produk</th><th className="text-right">Target</th>
                <th className="text-right">Good</th><th className="text-right">Reject</th><th className="text-right">Selisih</th>
                <th className="text-right">Upah</th><th>Status</th>
              </tr></thead>
              <tbody>
                {(prod?.rows || []).map((r) => (
                  <tr key={r.prod_no} className="border-b">
                    <td className="py-2 px-4">{formatDate(r.date)}</td>
                    <td className="font-mono text-xs">{r.prod_no}</td>
                    <td>{r.product_name}</td>
                    <td className="text-right font-mono">{num(r.target_dus)}</td>
                    <td className="text-right font-mono text-emerald-700">{num(r.good_dus)}</td>
                    <td className="text-right font-mono text-rose-700">{num(r.reject_dus)}</td>
                    <td className={`text-right font-mono ${r.selisih_dus ? "text-rose-700 font-bold" : ""}`}>{num(r.selisih_dus)}</td>
                    <td className="text-right font-mono">{rupiah(r.wage_total)}</td>
                    <td><ProdStatusBadge status={r.status} /></td>
                  </tr>
                ))}
                {prod && prod.rows.length > 0 && (
                  <tr className="bg-slate-50 font-bold">
                    <td className="py-2 px-4" colSpan={3}>TOTAL</td>
                    <td className="text-right font-mono">{num(prod.total.target)}</td>
                    <td className="text-right font-mono">{num(prod.total.good)}</td>
                    <td className="text-right font-mono">{num(prod.total.reject)}</td>
                    <td></td>
                    <td className="text-right font-mono" data-testid="rep-production-total-wage">{rupiah(prod.total.wage)}</td>
                    <td></td>
                  </tr>
                )}
                {prod && prod.rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="wages" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            {(wages?.summary || []).map((s) => (
              <Card key={s.operator} data-testid={`wage-summary-${s.operator}`}>
                <div className="p-4">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{s.operator}</div>
                  <div className="font-mono font-bold text-xl mt-1">{rupiah(s.wage)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{num(s.good)} dus good · {num(s.reject)} reject</div>
                </div>
              </Card>
            ))}
          </div>
          <Card>
            <table className="w-full text-sm" data-testid="rep-wages">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Tanggal</th><th>Produksi</th><th>Produk</th><th>Operator</th><th className="text-right">Target</th>
                <th className="text-right">Good</th><th className="text-right">Reject</th><th className="text-right">Tarif</th><th className="text-right">Upah</th>
              </tr></thead>
              <tbody>
                {(wages?.rows || []).map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-4">{formatDate(r.date)}</td>
                    <td className="font-mono text-xs">{r.prod_no}</td>
                    <td>{r.product_name}</td>
                    <td>{r.operator} <span className="text-xs text-slate-400">(#{r.package})</span></td>
                    <td className="text-right font-mono">{num(r.target_dus)}</td>
                    <td className="text-right font-mono text-emerald-700">{num(r.good_dus)}</td>
                    <td className="text-right font-mono text-rose-700">{num(r.reject_dus)}</td>
                    <td className="text-right font-mono">{rupiah(r.wage_rate)}</td>
                    <td className="text-right font-mono font-bold">{rupiah(r.wage)}</td>
                  </tr>
                ))}
                {wages && wages.rows.length > 0 && (
                  <tr className="bg-slate-50 font-bold">
                    <td className="py-2 px-4" colSpan={8}>GRAND TOTAL (upah = good × tarif, reject tidak dibayar)</td>
                    <td className="text-right font-mono" data-testid="rep-wages-grand-total">{rupiah(wages.grand_total)}</td>
                  </tr>
                )}
                {wages && wages.rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="materials" className="mt-4">
          <Card>
            <table className="w-full text-sm" data-testid="rep-materials">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Tanggal</th><th>Produksi</th><th>Bahan</th><th>Tipe</th>
                <th className="text-right">Teoritis</th><th className="text-right">Diambil</th><th className="text-right">Terpakai</th>
                <th className="text-right">Sisa</th><th className="text-right">Waste</th><th className="text-right">Selisih</th>
              </tr></thead>
              <tbody>
                {(materials?.rows || []).map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-4">{formatDate(r.date)}</td>
                    <td className="font-mono text-xs">{r.prod_no}</td>
                    <td>{r.sku} · {r.name}</td>
                    <td>{r.material_type === "consumable" ? <Badge className="bg-amber-100 text-amber-800 font-normal">Consumable</Badge> : <Badge className="bg-slate-100 text-slate-600 font-normal">Countable</Badge>}</td>
                    <td className="text-right font-mono">{num(r.theoretical_qty)}</td>
                    <td className="text-right font-mono">{num(r.issued_qty)}</td>
                    <td className="text-right font-mono">{num(r.used_qty)}</td>
                    <td className="text-right font-mono">{num(r.returned_qty)}</td>
                    <td className="text-right font-mono">{num(r.waste_qty)}</td>
                    <td className={`text-right font-mono ${r.selisih ? "text-rose-700 font-bold" : ""}`}>{num(r.selisih)}</td>
                  </tr>
                ))}
                {materials && materials.rows.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="waste" className="mt-4">
          <Card>
            <table className="w-full text-sm" data-testid="rep-waste">
              <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Tanggal</th><th>Produksi</th><th>Bahan</th><th className="text-right">Qty</th>
                <th className="text-right">Nilai</th><th>Alasan</th><th>Dibuat</th><th>Disetujui</th><th>Status</th>
              </tr></thead>
              <tbody>
                {(waste?.rows || []).map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-4">{formatDate(r.date)}</td>
                    <td className="font-mono text-xs">{r.prod_no}</td>
                    <td>{r.sku} · {r.name}</td>
                    <td className="text-right font-mono">{num(r.qty)} {r.unit}</td>
                    <td className="text-right font-mono">{rupiah(r.value)}</td>
                    <td>{r.reason}</td>
                    <td className="text-xs text-slate-500">{r.created_by}</td>
                    <td className="text-xs text-slate-500">{r.approved_by || "—"}</td>
                    <td>
                      {r.status === "PENDING" && <Badge className="bg-amber-100 text-amber-800 font-normal">Pending</Badge>}
                      {r.status === "APPROVED" && <Badge className="bg-emerald-100 text-emerald-800 font-normal">Approved</Badge>}
                      {r.status === "REJECTED" && <Badge className="bg-rose-100 text-rose-800 font-normal">Rejected</Badge>}
                    </td>
                  </tr>
                ))}
                {waste && waste.rows.length > 0 && (
                  <tr className="bg-slate-50 font-bold">
                    <td className="py-2 px-4" colSpan={4}>TOTAL WASTE (approved)</td>
                    <td className="text-right font-mono" data-testid="rep-waste-total">{rupiah(waste.total_value)}</td>
                    <td colSpan={4}></td>
                  </tr>
                )}
                {waste && waste.rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
